import * as React from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";

import { useOnlineStatus } from "@/hooks/useOnlineStatus";
import { useOutbox } from "@/hooks/useOutbox";
import { useStorageCapability } from "@/hooks/useStorageCapability";
import { useStorageEstimate } from "@/hooks/useStorageEstimate";
import {
  buildTimeclockFormData,
  getCostCodes,
  getCustomers,
  getEntries,
  submitTimeclockSync,
  toSyncDelayMsInt,
} from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { clientLog } from "@/lib/client-logger";
import { db } from "@/lib/db";
import { enqueuePunch } from "@/lib/outbox";
import { requestSync } from "@/lib/syncScheduler";
import { getDecision, getNetworkState, logEvent, startTrace } from "@/lib/telemetry";
import { getLastNDaysRange, nowForDatetimeLocal } from "@/lib/time";
import { compressSelfieForUpload, createObjectUrlSafe } from "@/pages/dashboard/employee/clockHelpers";
import {
  checkBackendReachable,
  errorMessage,
  isStorageFailure,
  localSaveFailureMessage,
  unreachable,
  type ReachabilityResult,
} from "@/pages/dashboard/offlineSave";

type Geo = {
  lat: number;
  lon: number;
  accuracy: number;
  timestamp: string;
};

function _getNow() { return performance.now(); }
function _getUuid() { return (crypto as any).randomUUID ? (crypto as any).randomUUID() : ("u" + performance.now()); }
function _getIso() { return new Date().toISOString(); }

export function useClockViewModel() {
  const auth = useAuth();
  const queryClient = useQueryClient();
  const user = auth.user;
  const online = useOnlineStatus();
  const { degraded: storageDegraded } = useStorageCapability();
  const { est: storageEst, refresh: refreshStorage } = useStorageEstimate();
  const [costCodeState, setCostCodeState] = React.useState({ id: "", name: "", search: "" });
  const activeCostCodeId = costCodeState.id;
  const activeCostCodeName = costCodeState.name;
  const costCodeSearch = costCodeState.search;
  const setActiveCostCodeId = (v: string) => setCostCodeState(s => ({...s, id: v}));
  const setActiveCostCodeName = (v: string) => setCostCodeState(s => ({...s, name: v}));
  const setCostCodeSearch = (v: string) => setCostCodeState(s => ({...s, search: v}));
  const [costCodeOpen, setCostCodeOpen] = React.useState(false);
  const costCodeRef = React.useRef<HTMLDivElement>(null);
  const [task, setTask] = React.useState("");
  const [notes, setNotes] = React.useState("");
  const [mealsBreaks, setMealsBreaks] = React.useState("");
  const [breakNotes, setBreakNotes] = React.useState("");
  const [injured, setInjured] = React.useState("");
  const [injuryNotes, setInjuryNotes] = React.useState("");
  const [customerState, setCustomerState] = React.useState({ id: "", name: "", search: "" });
  const customerId = customerState.id;
  const customerName = customerState.name;
  const customerSearch = customerState.search;
  const setCustomerId = (v: string) => setCustomerState(s => ({...s, id: v}));
  const setCustomerName = (v: string) => setCustomerState(s => ({...s, name: v}));
  const setCustomerSearch = (v: string) => setCustomerState(s => ({...s, search: v}));
  const [customerOpen, setCustomerOpen] = React.useState(false);
  const customerRef = React.useRef<HTMLDivElement>(null);
  const synergyEmployee = user?.email ?? "";
  const [action, setAction] = React.useState<"clock_in" | "clock_out">("clock_in");
  const [geoStatus, setGeoStatus] = React.useState<"idle" | "getting" | "ok" | "err">("idle");
  const [geo, setGeo] = React.useState<Geo | null>(null);
  /** Location errors only — never shared with the camera pane. */
  const [geoError, setGeoError] = React.useState("");
  const [selfieBlob, setSelfieBlob] = React.useState<Blob | null>(null);
  const [selfiePreview, setSelfiePreview] = React.useState("");
  const [cameraStatus, setCameraStatus] = React.useState<"idle" | "starting" | "ready" | "captured" | "err">("idle");
  const [cameraError, setCameraError] = React.useState("");
  const [selfieFromLiveCamera, setSelfieFromLiveCamera] = React.useState(false);
  const videoRef = React.useRef<HTMLVideoElement | null>(null);
  const streamRef = React.useRef<MediaStream | null>(null);
  const nativeInputRef = React.useRef<HTMLInputElement | null>(null);
  const isMobile = React.useMemo(() => {
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
      ("ontouchstart" in window) ||
      (navigator.maxTouchPoints > 2);
  }, []);
  React.useEffect(() => {
    return () => stopCamera();
  }, []);

  React.useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (customerRef.current && !customerRef.current.contains(event.target as Node)) {
        setCustomerOpen(false);
      }
      if (costCodeRef.current && !costCodeRef.current.contains(event.target as Node)) {
        setCostCodeOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const {
    data: costCodesData,
    refetch: refetchCostCodes,
  } = useQuery({
    queryKey: ["cost-codes", costCodeSearch],
    queryFn: () => getCostCodes(costCodeSearch),
    enabled: !!user && (costCodeSearch.length > 1 || costCodeOpen),
  });

  const {
    data: customersData,
    refetch: refetchCustomers,
  } = useQuery({
    queryKey: ["customers", customerSearch],
    queryFn: () => getCustomers(customerSearch),
    enabled: !!user && (customerSearch.length > 1 || customerOpen),
  });

  const clockRecentParams = React.useMemo(() => {
    const params = new URLSearchParams();
    if (user) params.set("user_id", String(user.id));
    const r = getLastNDaysRange(7);
    params.set("from_date", `${r.from}T00:00:00`);
    params.set("to_date", `${r.to}T23:59:59`);
    params.set("limit", "6");
    params.set("offset", "0");
    return params;
  }, [user]);

  const { data: clockRecentEntriesData } = useQuery({
    queryKey: ["recent-entries", clockRecentParams.toString()],
    queryFn: () => getEntries(clockRecentParams),
    enabled: !!user,
  });

  // Punch submission: best-effort write to IndexedDB first (for offline queue + UI).
  // If that fails (quota, private mode, etc.) and the backend is actually
  // reachable, fall back to a direct POST using client_uuid for idempotency.
  // This prevents hard failures for users with no local storage while still
  // giving full offline durability when IndexedDB succeeds.
  const [punchSubmitting, setPunchSubmitting] = React.useState(false);
  const [punchSaved, setPunchSaved] = React.useState<{ offline: boolean; direct?: boolean } | null>(null);
  const [punchError, setPunchError] = React.useState<string | null>(null);

  function resetPunchForm() {
    // Only clear per-punch transient fields. Cost code + customer/site are
    // intentionally kept (or restored from last) so the next clock-in
    // pre-fills the previous values the user asked for.
    setTask("");
    setNotes("");
    setMealsBreaks("");
    setBreakNotes("");
    setInjured("");
    setInjuryNotes("");
    // Do NOT clear customer/costCode here — they act as "last used" defaults.
    // setCustomerId(""); setCustomerName(""); setCustomerSearch("");
    setSelfieBlob(null);
    setSelfiePreview("");
    setSelfieFromLiveCamera(false);
    stopCamera();
    setCameraStatus("idle");
    setCameraError("");
    setGeo(null);
    setGeoStatus("idle");
    setGeoError("");
  }
  const latestOwnEntry = (clockRecentEntriesData ?? [])[0];
  const outbox = useOutbox(user?.id);
  // Prefer the locally recorded punch when it is newer than what the server
  // has reported back — it is the source of truth while punches are queued
  // offline or the entries query hasn't refetched yet.
  const serverActionAt = latestOwnEntry
    ? Date.parse(latestOwnEntry.clock_out_time ?? latestOwnEntry.recorded_time)
    : Number.NEGATIVE_INFINITY;
  const effectiveAction =
    outbox.lastPunch && (Number.isNaN(serverActionAt) || outbox.lastPunch.createdAt >= serverActionAt)
      ? outbox.lastPunch.action
      : latestOwnEntry?.action;
  const isClocked = effectiveAction === "clock_in";
  const isExempt = user?.qb_payroll_code === "E";
  const clockAction: "clock_in" | "clock_out" = isExempt ? "clock_in" : action;

  React.useEffect(() => {
    if (isExempt && action !== "clock_in") {
      setAction("clock_in");
    }
  }, [isExempt, action]);

  // For clock-out, display the cost code & customer from the *actual* open clock-in
  // entry that the backend will match this out to. This ensures the UI always shows
  // what was used on that specific clock-in, even if the user later changed the
  // form dropdowns (or "last used" state). The values are still included in the
  // submitted payload (harmless, since backend ignores them on out), but the
  // display must match the matched clock-in to avoid questions.
  const clockOutShiftContext = React.useMemo(() => {
    if (clockAction !== "clock_out" || !isClocked) return null;

    // Prefer server data for the current open shift (most authoritative)
    if (latestOwnEntry && latestOwnEntry.action === "clock_in" && !latestOwnEntry.clock_out_time) {
      return {
        costCodeName: latestOwnEntry.cost_code_name || activeCostCodeName,
        customerName: latestOwnEntry.customer_name || customerName,
      };
    }

    // Fallback: clock-in may still be pending (in outbox). The form state
    // captured the values at enqueue time, so use it.
    return {
      costCodeName: activeCostCodeName,
      customerName: customerName,
    };
  }, [clockAction, isClocked, latestOwnEntry, activeCostCodeName, customerName]);

  // Load last used values from IndexedDB (for offline persistence) and fall back
  // to the most recent server entry. Must be after latestOwnEntry declaration.
  const hasLoadedLastUsedRef = React.useRef(false);
  React.useEffect(() => {
    if (!user?.id) return;
    let cancelled = false;

    (async () => {
      try {
        // 1. Try persisted last-used from kv store (survives refresh)
        if (!hasLoadedLastUsedRef.current) {
          const [lastCost, lastCust] = await Promise.all([
            db.kv.get(`lastCostCode:${user.id}`).catch(() => undefined),
            db.kv.get(`lastCustomer:${user.id}`).catch(() => undefined),
          ]);
          if (cancelled) return;

          if (lastCost?.value) {
            const v = lastCost.value as { id?: string; name?: string };
            if (v.id) setActiveCostCodeId(v.id);
            if (v.name) setActiveCostCodeName(v.name);
          }
          if (lastCust?.value) {
            const v = lastCust.value as { id?: string; name?: string };
            if (v.id) setCustomerId(v.id);
            if (v.name) setCustomerName(v.name);
          }
          hasLoadedLastUsedRef.current = true;
        }

        // 2. Fallback / enhancement from last server entry (when data is available)
        // This is critical: previous entries have both id + name. We must restore
        // the *ids* (not just names) otherwise the form shows prefilled (by name)
        // but submit omits cost_code_id/customer_id → they become "unassigned".
        if (latestOwnEntry) {
          if (latestOwnEntry.cost_code_id != null && !costCodeState.id) {
            setActiveCostCodeId(String(latestOwnEntry.cost_code_id));
          }
          if (!costCodeState.name && latestOwnEntry.cost_code_name) {
            setActiveCostCodeName(latestOwnEntry.cost_code_name);
          }
          if (latestOwnEntry.customer_id != null && !customerState.id) {
            setCustomerId(String(latestOwnEntry.customer_id));
          }
          if (!customerState.name && latestOwnEntry.customer_name) {
            setCustomerName(latestOwnEntry.customer_name);
          }
        }
      } catch {
        // best effort only
      }
    })();

    return () => { cancelled = true; };
  }, [user?.id, latestOwnEntry]);

  // action defaults to clock_in; previous effect to derive from last entry removed to avoid derived-state + chain updates
  /**
   * Safari / iOS WebKit / Vivaldi often time out on high-accuracy + maxAge:0.
   * Chrome/Edge/Opera/Firefox usually succeed because they use Google (or
   * similar) network location and return a fix quickly on desktop.
   */
  function isPickyGeolocationBrowser(): boolean {
    const ua = navigator.userAgent || "";
    const isVivaldi = /Vivaldi/i.test(ua);
    // Safari desktop/iOS: has Safari, not Chromium/Firefox branding
    const isSafari =
      /Safari/i.test(ua) && !/Chrome|Chromium|CriOS|Edg|EdgiOS|OPR|Firefox|FxiOS|Vivaldi/i.test(ua);
    // iOS browsers all use WebKit under the hood
    const isIosWebKit = /iP(hone|ad|od)/.test(ua);
    return isSafari || isVivaldi || isIosWebKit;
  }

  function requestBrowserPosition(options: PositionOptions): Promise<GeolocationPosition> {
    return new Promise((resolve, reject) => {
      navigator.geolocation.getCurrentPosition(resolve, reject, options);
    });
  }

  function geoErrorMessage(err: GeolocationPositionError | unknown): string {
    if (err && typeof err === "object" && "code" in err) {
      const ge = err as GeolocationPositionError;
      if (ge.code === ge.PERMISSION_DENIED) {
        return "Location permission was denied. Allow this site in browser location settings (Safari: Settings → Websites → Location).";
      }
      if (ge.code === ge.POSITION_UNAVAILABLE) {
        return "Location is unavailable. On Mac, enable System Settings → Privacy & Security → Location Services for the browser.";
      }
      if (ge.code === ge.TIMEOUT) {
        return "Location timed out. Safari/Vivaldi use OS location (not Google Wi‑Fi like Chrome). Enable Location Services, allow this site, then try again.";
      }
    }
    return "Could not retrieve location.";
  }

  function buildGeoAttempts(preferFresh: boolean): PositionOptions[] {
    const picky = isPickyGeolocationBrowser();
    // Picky engines: start soft (network/Wi‑Fi, allow cache) so we don't burn
    // the whole timeout waiting for a GPS chip that does not exist on desktop.
    // Chrome-family: try high-accuracy first (usually fast via Google Location).
    if (picky) {
      return preferFresh
        ? [
            { enableHighAccuracy: false, timeout: 12000, maximumAge: 60000 },
            { enableHighAccuracy: true, timeout: 10000, maximumAge: 30000 },
            { enableHighAccuracy: false, timeout: 15000, maximumAge: 300000 },
          ]
        : [
            { enableHighAccuracy: false, timeout: 12000, maximumAge: 60000 },
            { enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 },
          ];
    }
    return preferFresh
      ? [
          { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 },
          { enableHighAccuracy: true, timeout: 10000, maximumAge: 30000 },
          { enableHighAccuracy: false, timeout: 12000, maximumAge: 60000 },
        ]
      : [
          { enableHighAccuracy: true, timeout: 12000, maximumAge: 30000 },
          { enableHighAccuracy: false, timeout: 12000, maximumAge: 60000 },
        ];
  }

  async function acquireGeo(opts?: { preferFresh?: boolean }): Promise<Geo | null> {
    if (!navigator.geolocation) {
      setGeoStatus("err");
      setGeoError("Geolocation is not supported by this browser.");
      return null;
    }
    if (!window.isSecureContext) {
      setGeoStatus("err");
      setGeoError("Location requires a secure (HTTPS) connection.");
      return null;
    }

    setGeoStatus("getting");
    setGeoError("");
    const gpsStart = _getNow();
    const attempts = buildGeoAttempts(!!opts?.preferFresh);

    let lastError: unknown = null;
    for (const options of attempts) {
      try {
        const position = await requestBrowserPosition(options);
        const gpsMs = Math.round(_getNow() - gpsStart);
        // Keep full browser precision (Number is ~15 significant digits). Do not
        // round here — only the on-screen preview may format for readability.
        // Stored/submitted values use String(lat/lon) so long decimals are preserved.
        const next: Geo = {
          lat: position.coords.latitude,
          lon: position.coords.longitude,
          accuracy: position.coords.accuracy,
          timestamp: new Date(position.timestamp || Date.now()).toISOString(),
        };
        setGeo(next);
        setGeoStatus("ok");
        setGeoError("");
        try {
          const net = getNetworkState();
          const t = startTrace("gps_acquire");
          t.stop({ gps_ms: gpsMs, network: net.type, high_accuracy: options.enableHighAccuracy ? 1 : 0 });
          if (getDecision().analyticsEnabled) {
            logEvent("gps_acquired", { gps_ms: gpsMs, network_type: net.type });
          }
        } catch {
          /* telemetry best-effort */
        }
        return next;
      } catch (err) {
        lastError = err;
        // Permission denied will not succeed on a softer attempt — stop early.
        if (
          err &&
          typeof err === "object" &&
          "code" in err &&
          (err as GeolocationPositionError).code === (err as GeolocationPositionError).PERMISSION_DENIED
        ) {
          break;
        }
      }
    }

    const gpsMs = Math.round(_getNow() - gpsStart);
    setGeoStatus("err");
    setGeoError(geoErrorMessage(lastError));
    try {
      startTrace("gps_acquire").stop({ gps_ms: gpsMs, error: 1 });
    } catch {
      /* ignore */
    }
    return null;
  }

  async function getLocation() {
    return acquireGeo({ preferFresh: false });
  }

  function stopCamera() {
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
    if (videoRef.current) videoRef.current.srcObject = null;
  }

  async function startCamera() {
    if (!navigator.mediaDevices?.getUserMedia) {
      setCameraStatus("err");
      setCameraError("Camera access is not available in this browser.");
      return;
    }
    if (!window.isSecureContext) {
      setCameraStatus("err");
      setCameraError("Camera access requires a secure (HTTPS) connection.");
      return;
    }
    setCameraStatus("starting");
    setCameraError("");
    setSelfieBlob(null);
    setSelfiePreview("");
    try {
      stopCamera();
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "user", width: { ideal: 1280 }, height: { ideal: 720 } },
        audio: false,
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }
      setCameraStatus("ready");
    } catch (err) {
      setCameraStatus("err");
      if (err instanceof DOMException) {
        if (err.name === "NotAllowedError" || err.name === "PermissionDeniedError") {
          setCameraError("Camera permission was denied. Please allow camera access in your browser settings.");
        } else if (err.name === "NotFoundError") {
          setCameraError("No camera found on this device.");
        } else if (err.name === "NotReadableError" || err.name === "TrackStartError") {
          setCameraError("Camera is already in use by another application.");
        } else {
          setCameraError(`Camera error: ${err.name}. ${err.message}`);
        }
      } else {
        setCameraError("Camera permission is required to capture a photo.");
      }
    }
  }

  function getAggressiveCompressionParams() {
    if (!storageEst || !storageEst.isLow) {
      return { maxDimension: 960, quality: 0.72 };
    }
    // When storage is low, be more aggressive to avoid enqueue failures
    return { maxDimension: 640, quality: 0.55 };
  }

  async function captureSelfie() {
    const video = videoRef.current;
    if (!video || video.readyState < 2) {
      setCameraStatus("err");
      setCameraError("Camera is not ready yet.");
      return;
    }
    const canvas = document.createElement("canvas");
    // Cap initial capture resolution for consistency across phones/tablets/laptops.
    // The real heavy lifting + final size control happens in compressSelfieForUpload.
    const MAX_CAPTURE = 1280;
    let cw = video.videoWidth || 1280;
    let ch = video.videoHeight || 720;
    if (cw > MAX_CAPTURE || ch > MAX_CAPTURE) {
      if (cw > ch) {
        ch = Math.round(ch * (MAX_CAPTURE / cw));
        cw = MAX_CAPTURE;
      } else {
        cw = Math.round(cw * (MAX_CAPTURE / ch));
        ch = MAX_CAPTURE;
      }
    }
    canvas.width = cw;
    canvas.height = ch;
    const context = canvas.getContext("2d");
    if (!context) {
      setCameraStatus("err");
      setCameraError("Could not capture camera frame.");
      return;
    }
    context.drawImage(video, 0, 0, canvas.width, canvas.height);
    let rawBlob: Blob | null = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob(resolve, "image/jpeg", 0.82)
    );
    if (!rawBlob) {
      // Extreme fallback: try once more at very low quality / same size.
      rawBlob = await new Promise<Blob | null>((resolve) =>
        canvas.toBlob(resolve, "image/jpeg", 0.5)
      );
    }
    if (!rawBlob) {
      setCameraStatus("err");
      setCameraError("Could not create photo.");
      return;
    }
    const camStart = _getNow();
    let finalBlob: Blob = rawBlob;
    try {
      finalBlob = await compressSelfieForUpload(rawBlob);
    } catch {
      // Never let compression prevent submission. Use raw captured frame as last resort.
      finalBlob = rawBlob;
    }
    const compMs = Math.round(_getNow() - camStart);
    setSelfieBlob(finalBlob);
    setSelfiePreview(createObjectUrlSafe(finalBlob));
    setSelfieFromLiveCamera(true);
    stopCamera();
    setCameraStatus("captured");
    setCameraError("");
    clientLog("info", "photo:live_capture:success", {
      rawBytes: rawBlob.size,
      finalBytes: finalBlob.size,
      compressed: finalBlob !== rawBlob,
      durationMs: compMs,
    });

    // Telemetry: camera + compress + size (critical for cellular perf)
    try {
      const net = getNetworkState();
      const t = startTrace("camera_capture");
      t.stop({ capture_ms: compMs, compress_ms: compMs, selfie_kb: Math.round(finalBlob.size / 1024), network: net.type });
      logEvent("selfie_captured", { capture_ms: compMs, selfie_kb: Math.round(finalBlob.size / 1024), network_type: net.type });
    } catch {}
  }

  async function handleNativePhoto(event: React.ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    if (!file) return;
    if (!isMobile) {
      setCameraError("File uploads are not allowed for selfie verification on desktop. Please use the live camera.");
      event.target.value = "";
      return;
    }
    try {
      const compressed = await compressSelfieForUpload(file);
      setSelfieBlob(compressed);
      setSelfiePreview(createObjectUrlSafe(compressed));
      setCameraStatus("captured");
      setCameraError("");
      clientLog("info", "photo:native_capture:success", {
        inputType: file.type || "unknown",
        inputBytes: file.size,
        outputBytes: compressed.size,
        compressed: compressed !== file,
      });
    } catch {
      // Fallback for DNG/RAW/HEIC and other formats the browser can't decode
      setSelfieBlob(file);
      setSelfiePreview(createObjectUrlSafe(file));
      setCameraStatus("captured");
      setCameraError("");
      clientLog("warn", "photo:native_capture:compression_fallback", {
        inputType: file.type || "unknown",
        inputBytes: file.size,
      });
    }
    // reset input so the same file can be selected again
    event.target.value = "";
  }

  async function handleClockSubmit(event: React.FormEvent) {
    event.preventDefault();
    if (!user) return;
    const flowTrace = startTrace("clock_in_flow");
    const flowStart = _getNow();
    const location = geo ?? (await getLocation());
    if (!location) {
      try { flowTrace.stop({ aborted: "no_location" }); } catch {}
      return;
    }
    if (!selfieBlob) {
      try { flowTrace.stop({ aborted: "no_selfie" }); } catch {}
      return;
    }
    if (!isMobile && !selfieFromLiveCamera) {
      setCameraError("Live camera capture is required for selfie verification on desktop. File uploads are not permitted.");
      try { flowTrace.stop({ aborted: "upload_not_allowed" }); } catch {}
      return;
    }

    // Proactively request persistent storage and check quota before writing large selfie.
    // This is a hint only — we still attempt the enqueue so that the IDB-first +
    // direct-backend fallback logic (below) can decide. Hard bail removed to avoid
    // blocking users whose heuristic says "low" but who can still submit directly.
    try {
      if (navigator.storage?.persist) {
        await navigator.storage.persist();
      }
      const usage = storageEst?.usage ?? 0;
      const quota = storageEst?.quota ?? 0;
      const lowFromHook = storageEst?.isLow;
      if (quota > 0 && (lowFromHook || (usage / quota > 0.75 || (quota - usage) < 8 * 1024 * 1024))) {
        setPunchError("Low on device storage for photos — will try to submit directly if local storage fails.");
        refreshStorage();
        // Do not return; proceed to enqueue attempt + fallback.
      }
      // If hook not available or stale, do a live check (same: warn but continue)
      if (!storageEst && navigator.storage?.estimate) {
        const est = await navigator.storage.estimate();
        const u = est.usage ?? 0;
        const q = est.quota ?? 0;
        if (q > 0 && (u / q > 0.75 || (q - u) < 8 * 1024 * 1024)) {
          setPunchError("Low on device storage for photos — will try to submit directly if local storage fails.");
          // continue
        }
      }
    } catch {
      // non-fatal preflight
    }

    const currentTime = nowForDatetimeLocal();
    const fields: Record<string, string> = {
      action: clockAction,
      technician_name: user.full_name ?? "",
      company_name: user.company_name ?? "",
      site_name: customerName || "",
      synergy_employee_name: user.email ?? "",
      current_time: currentTime,
      current_time_iso: new Date(currentTime).toISOString(),
      tz_name: Intl.DateTimeFormat().resolvedOptions().timeZone || "",
      tz_offset_minutes: String(new Date().getTimezoneOffset()),
      gps_lat: String(location.lat),
      gps_lon: String(location.lon),
      gps_accuracy: String(location.accuracy),
      gps_ts: location.timestamp,
      submitted_ts: new Date().toISOString(),
      task,
      notes,
    };
    if (clockAction === "clock_out") {
      if (mealsBreaks) fields.meals_breaks = mealsBreaks;
      if (breakNotes) fields.break_notes = breakNotes;
      if (injured) fields.injured = injured;
      if (injuryNotes) fields.injury_notes = injuryNotes;
    }
    if (activeCostCodeId) fields.cost_code_id = activeCostCodeId;
    if (customerId) fields.customer_id = customerId;

    const punchCreatedAt = _getNow();
    const clientUuid = _getUuid();

    setPunchError(null);
    setPunchSaved(null);
    setPunchSubmitting(true);
    clientLog("info", "punch:submit:start", {
      clockAction,
      online,
      hasSelfie: !!selfieBlob,
      selfieBytes: selfieBlob.size,
      hasGeo: !!location,
      storagePercent: storageEst?.percent,
      storageLow: storageEst?.isLow,
    });

    let usedDirectFallback = false;

    const submitDirect = async (storageError?: string) => {
      const fd = buildTimeclockFormData({
        fields,
        clientUuid,
        selfieBlob,
        selfieName: `selfie-${punchCreatedAt}.jpg`,
        syncDelayMs: _getNow() - punchCreatedAt,
      });
      await submitTimeclockSync(fd);
      usedDirectFallback = true;
      clientLog("warn", "punch:submit:direct_fallback_success", {
        action: clockAction,
        storageError: storageError || "degraded_mode",
        storageDegraded,
      });
    };

    // Private browsing / broken IDB: never touch the outbox — go straight to the API.
    if (storageDegraded) {
      if (!online) {
        setPunchError(
          "Offline punches are unavailable in private browsing or restricted storage. Open this app in normal Safari (or Add to Home Screen), go online, and try again.",
        );
        setPunchSubmitting(false);
        return;
      }
      try {
        await submitDirect("preemptive_degraded");
        clientLog("info", "punch:submit:online_only", { action: clockAction });
      } catch (directErr) {
        const directMsg = directErr instanceof Error ? directErr.message : "Network error";
        clientLog("error", "punch:submit:online_only_failed", {
          action: clockAction,
          directError: directMsg,
        });
        setPunchError(`Could not submit the punch to the server: ${directMsg}`);
        setPunchSubmitting(false);
        return;
      }
    } else {
      try {
        await enqueuePunch({
          action: clockAction,
          fields,
          selfieBlob,
          selfieName: `selfie-${punchCreatedAt}.jpg`,
          turnstileToken: null,
          userId: user.id,
        });
        clientLog("info", "punch:submit:queued", {
          action: clockAction,
          online,
          selfieBytes: selfieBlob.size,
        });
      } catch (error) {
        // IDB write failed (quota, private browsing, transient connection, etc.).
        // If the backend is really reachable, fall back to direct submit so the
        // user is not blocked by local storage failures.
        const msg = errorMessage(error);
        const looksLikeStorage = isStorageFailure(error);

        const reachability: ReachabilityResult = looksLikeStorage
          ? await checkBackendReachable().catch(() => unreachable("the server reachability check failed"))
          : unreachable("the error was not a storage failure");

        if (looksLikeStorage && reachability.reachable) {
          try {
            await submitDirect(msg);
          } catch (directErr) {
            // Both paths failed.
            const directMsg = directErr instanceof Error ? directErr.message : "Network error";
            clientLog("error", "punch:submit:direct_fallback_failed", {
              action: clockAction,
              storageError: msg,
              directError: directMsg,
            });
            setPunchError(`Could not save the punch on this device (${msg}). Direct server submit also failed: ${directMsg}.`);
            setPunchSubmitting(false);
            return;
          }
        } else if (looksLikeStorage) {
          clientLog("error", "punch:submit:local_save_failed_offline_or_unreachable", {
            action: clockAction,
            storageError: msg,
            reachability,
          });
          setPunchError(localSaveFailureMessage("punch", msg, reachability));
          setPunchSubmitting(false);
          return;
        } else {
          // Not a storage issue, or offline — treat as hard failure (original behavior).
          const full = msg.toLowerCase().includes("connection") || msg.toLowerCase().includes("lost")
            ? `Could not save the punch on this device: ${msg}. Refresh the page to try again.`
            : `Could not save the punch on this device: ${msg} Free up storage space and try again.`;
          clientLog("error", "punch:submit:local_save_failed", {
            action: clockAction,
            error: msg,
          });
          setPunchError(full);
          setPunchSubmitting(false);
          return;
        }
      }
    }

    setPunchSubmitting(false);

    if (usedDirectFallback) {
      setPunchSaved({ offline: false, direct: true });
      // Direct success — server already has it. Make recent entries / clock state update.
      void queryClient.invalidateQueries({ queryKey: ["entries"] });
      void queryClient.invalidateQueries({ queryKey: ["recent-entries"] });
      void queryClient.invalidateQueries({ queryKey: ["entries-count"] });
      void queryClient.invalidateQueries({ queryKey: ["recent-entries-count"] });
      refreshStorage();
      // No need to requestSync; the item was never queued.
    } else {
      setPunchSaved({ offline: !online });
      refreshStorage();
      // A punch is intentionally saved locally first, even while online. Once
      // that durable write succeeds, kick the same immediate sync path the
      // manual Sync button uses so online punches do not wait for the timer.
      requestSync({ force: true });
    }

    const flowMs = Math.round(_getNow() - flowStart);
    const selfieKb = selfieBlob ? Math.round(selfieBlob.size / 1024) : 0;
    try {
      const net = getNetworkState();
      flowTrace.stop({ total_ms: flowMs, selfie_kb: selfieKb, network: net.type, role: user.role });
      logEvent("clock_action_submitted", {
        action: clockAction,
        duration_ms: flowMs,
        selfie_kb: selfieKb,
        network_type: net.type,
        user_role: user.role,
      });
    } catch {}

    setTimeout(() => {
      resetPunchForm();
    }, 1200);

    // Persist last used cost code + customer/site to IndexedDB so we can
    // pre-fill on next load / next session (offline-friendly).
    if (user?.id && !storageDegraded) {
      void (async () => {
        try {
          if (activeCostCodeId || activeCostCodeName) {
            await db.kv.put({
              key: `lastCostCode:${user.id}`,
              value: { id: activeCostCodeId, name: activeCostCodeName },
            });
          }
          if (customerId || customerName) {
            await db.kv.put({
              key: `lastCustomer:${user.id}`,
              value: { id: customerId, name: customerName },
            });
          }
        } catch {
          // best effort, non-fatal
        }
      })();
    }
  }

  const customerOptions = customersData ?? [];
  const costCodeOptions = costCodesData ?? [];


  return {
    user,
    storageDegraded,
    handleClockSubmit,
    isExempt,
    action,
    setAction,
    isClocked,
    clockAction,
    clockOutShiftContext,
    activeCostCodeName,
    customerName,
    costCodeRef,
    costCodeOpen,
    setCostCodeOpen,
    costCodeSearch,
    setCostCodeSearch,
    setActiveCostCodeId,
    setActiveCostCodeName,
    costCodeOptions,
    customerRef,
    customerOpen,
    setCustomerOpen,
    customerSearch,
    setCustomerSearch,
    setCustomerId,
    setCustomerName,
    customerOptions,
    synergyEmployee,
    task,
    setTask,
    notes,
    setNotes,
    mealsBreaks,
    setMealsBreaks,
    breakNotes,
    setBreakNotes,
    injured,
    setInjured,
    injuryNotes,
    setInjuryNotes,
    geoStatus,
    geo,
    geoError,
    acquireGeo,
    isMobile,
    nativeInputRef,
    handleNativePhoto,
    cameraStatus,
    videoRef,
    selfiePreview,
    selfieBlob,
    cameraError,
    setCameraError,
    startCamera,
    captureSelfie,
    selfieFromLiveCamera,
    punchError,
    punchSaved,
    punchSubmitting,
  };
}
